<div class="container">
  <h1>Settings</h1>
  
  <!-- API Keys Section -->
  <div class="card">
    <h2>API Keys</h2>
    <p class="subtitle">Manage your API keys for SDK and API integrations</p>
    
    <div *ngIf="loading" class="loading">Loading...</div>
    
    <div *ngIf="error" class="error-box">
      <p>{{ error }}</p>
    </div>
    
    <!-- Create New API Key -->
    <div class="form-section">
      <h3>Create New API Key</h3>
      <div class="form-group">
        <label for="newKeyName">Key Name</label>
        <input
          type="text"
          id="newKeyName"
          [(ngModel)]="newKeyName"
          placeholder="My API Key"
          class="form-input">
      </div>
      <div class="form-group">
        <label for="newKeyProject">Project (Optional)</label>
        <select id="newKeyProject" [(ngModel)]="newKeyProjectId" class="form-input">
          <option [value]="null">Org-level (all projects)</option>
          <option *ngFor="let project of projects" [value]="project.id">{{ project.name }}</option>
        </select>
      </div>
      <button class="btn btn-primary" (click)="createApiKey()" [disabled]="creating">
        {{ creating ? 'Creating...' : 'Create API Key' }}
      </button>
    </div>
    
    <!-- Display Newly Created Key -->
    <div *ngIf="newlyCreatedKey" class="api-key-box">
      <h3>🎉 API Key Created!</h3>
      <p><strong>Save this key now</strong> - you won't be able to see it again!</p>
      <div class="api-key-display">
        <code>{{ newlyCreatedKey }}</code>
        <button class="btn btn-secondary" (click)="copyNewKey()">Copy</button>
      </div>
      <button class="btn btn-primary" (click)="dismissNewKey()">Done</button>
    </div>
    
    <!-- List of API Keys -->
    <div class="api-keys-list" *ngIf="apiKeys.length > 0">
      <h3>Your API Keys</h3>
      <table class="keys-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Project</th>
            <th>Created</th>
            <th>Last Used</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let key of apiKeys">
            <td>{{ key.name || 'Unnamed' }}</td>
            <td>{{ getProjectName(key.project_id) || 'Org-level' }}</td>
            <td>{{ key.created_at | date:'short' }}</td>
            <td>{{ key.last_used_at ? (key.last_used_at | date:'short') : 'Never' }}</td>
            <td>
              <span [class]="key.revoked_at ? 'status-revoked' : 'status-active'">
                {{ key.revoked_at ? 'Revoked' : 'Active' }}
              </span>
            </td>
            <td>
              <button 
                class="btn btn-danger btn-sm" 
                (click)="revokeKey(key.id)"
                [disabled]="!!key.revoked_at || revoking === key.id">
                {{ key.revoked_at ? 'Revoked' : 'Revoke' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div *ngIf="apiKeys.length === 0 && !loading" class="empty-state">
      <p>No API keys yet. Create one above to get started.</p>
    </div>
  </div>
  
  <!-- Workspace / organization -->
  <div class="card" *ngIf="org">
    <h2>Workspace</h2>
    <p class="subtitle">Name shown in the app and on invoices. You can change it anytime (admin only).</p>
    <div class="form-section">
      <div class="form-group">
        <label for="orgNameDraft">Workspace name</label>
        <input
          type="text"
          id="orgNameDraft"
          [(ngModel)]="orgNameDraft"
          [disabled]="orgProfileSaving"
          class="form-input"
        />
      </div>
      <button
        type="button"
        class="btn btn-primary"
        (click)="saveOrgProfile()"
        [disabled]="orgProfileSaving || !orgNameDraft.trim() || orgNameDraft.trim() === org.name"
      >
        {{ orgProfileSaving ? 'Saving...' : 'Save name' }}
      </button>
    </div>
    <div class="org-info" style="margin-top: 1rem;">
      <p><strong>Status:</strong> {{ org.subscription_status }}</p>
      <p *ngIf="org.subscription_status === 'trial' && org.trial_ends_at">
        <strong>Access window ends:</strong> {{ org.trial_ends_at | date:'medium' }}
      </p>
    </div>
  </div>
  
  <!-- Members & Roles Section -->
  <div class="card">
    <div class="card-header">
      <h2>Members & Roles</h2>
      <button class="btn btn-sm btn-primary">Invite Member</button>
    </div>
    <div class="members-section">
      <p class="info-text">Role-based access control coming soon. Currently, all members have full access.</p>
      <div class="roles-info">
        <div class="role-item">
          <strong>Owner</strong>
          <p>Full access, can manage org settings and billing</p>
        </div>
        <div class="role-item">
          <strong>Admin</strong>
          <p>Can manage projects, policies, and members</p>
        </div>
        <div class="role-item">
          <strong>Member</strong>
          <p>Can view runs and usage, create API keys</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Data Handling Settings -->
  <div class="card">
    <div class="card-header">
      <h2>Data Handling</h2>
    </div>
    <div class="data-settings">
      <div class="setting-item">
        <div class="setting-info">
          <strong>Send Prompts to Control Plane</strong>
          <p>Allow Spectyra to receive full prompt content for optimization (API mode only)</p>
        </div>
        <label class="toggle">
          <input type="checkbox" checked>
          <span class="toggle-slider"></span>
        </label>
      </div>
      <div class="setting-item">
        <div class="setting-info">
          <strong>Send Agent Events</strong>
          <p>Stream agent events to Spectyra for telemetry and analytics</p>
        </div>
        <label class="toggle">
          <input type="checkbox" checked>
          <span class="toggle-slider"></span>
        </label>
      </div>
      <div class="setting-item">
        <div class="setting-info">
          <strong>Data Retention Period</strong>
          <p>How long to keep run data and events (for compliance)</p>
        </div>
        <select class="form-select">
          <option value="30">30 days</option>
          <option value="90">90 days</option>
          <option value="365">1 year</option>
          <option value="0">Indefinite</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Projects Section -->
  <div class="card" *ngIf="projects.length > 0">
    <h2>Projects</h2>
    <ul class="projects-list">
      <li *ngFor="let project of projects">
        <strong>{{ project.name }}</strong>
        <span class="project-id">ID: {{ project.id }}</span>
      </li>
    </ul>
  </div>
</div>
